"use client";

import { Truck } from "@/components/icons";
import { formatCentsWhole } from "@/lib/formatting/currency";
import { cn } from "@/lib/utils";
import { MapCards, PhoneCards } from "./landing-hero-map-cards";
import {
  type ActiveOrder,
  DELIVERED_PHASE,
  FADING_PHASE,
  MAP_ORDERS,
  type MapOrder,
  type MapPoint,
  STEP_MS,
  activeOrders,
  lanePath,
  useOrderTimeline,
} from "./landing-hero-orders";
import { US_DOT_PATH, US_MAP_HEIGHT, US_MAP_WIDTH } from "./landing-network-map";

const TRUCK_SECONDS = STEP_MS / 1000;
const TRUCK_PHASE = 4;

function Pulse() {
  return (
    <circle r={8} className="fill-brand/30 motion-reduce:hidden">
      <animate attributeName="r" values="8;22;8" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.9;0;0.9" dur="2s" repeatCount="indefinite" />
    </circle>
  );
}

function Pin({ point, filled, pulse }: { point: MapPoint; filled: boolean; pulse: boolean }) {
  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      {pulse ? <Pulse /> : null}
      <circle r={8} className={cn("stroke-surface", filled ? "fill-brand" : "fill-foreground")} strokeWidth={3} />
    </g>
  );
}

function NetworkLanes() {
  return (
    <g fill="none">
      {MAP_ORDERS.map((order) => (
        <path key={order.id} d={lanePath(order.from.point, order.to.point)} className="stroke-foreground/10" strokeWidth={2} />
      ))}
    </g>
  );
}

function TruckMarker({ path, at, moving }: { path: string; at: MapPoint; moving: boolean }) {
  return (
    <g transform={moving ? undefined : `translate(${at[0]} ${at[1]})`}>
      {moving ? <animateMotion path={path} dur={`${TRUCK_SECONDS}s`} fill="freeze" /> : null}
      <circle r={17} className="fill-surface stroke-brand" strokeWidth={3} />
      <Truck x={-10} y={-10} width={20} height={20} className="text-foreground" strokeWidth={2.25} />
    </g>
  );
}

function ActiveLane({ order, phase, animated }: { order: MapOrder; phase: number; animated: boolean }) {
  const path = lanePath(order.from.point, order.to.point);
  const moving = animated && phase === TRUCK_PHASE;
  return (
    <g fill="none" strokeLinecap="round" className={cn("transition-opacity duration-700", phase === FADING_PHASE && "opacity-0")}>
      {phase >= 2 ? <path d={path} className="stroke-brand/60 max-md:[stroke-width:6]" strokeWidth={2.5} strokeDasharray="2 7" /> : null}
      {phase >= TRUCK_PHASE ? (
        <path d={path} pathLength={1} className="stroke-brand max-md:[stroke-width:8]" strokeWidth={3.5} strokeDasharray="1 1">
          {moving ? <animate attributeName="stroke-dashoffset" from="1" to="0" dur={`${TRUCK_SECONDS}s`} fill="freeze" /> : null}
        </path>
      ) : null}
      <Pin point={order.to.point} filled={false} pulse={phase === 2} />
      <Pin point={order.from.point} filled pulse={phase === 0} />
      {phase >= TRUCK_PHASE ? <TruckMarker path={path} at={phase >= DELIVERED_PHASE ? order.to.point : order.from.point} moving={moving} /> : null}
    </g>
  );
}

function ExampleSummary() {
  return (
    <ul className="sr-only">
      {MAP_ORDERS.map((order) => (
        <li key={order.id}>
          {`Example order: ${order.quantity} ${order.noun} from ${order.from.city}, ${order.from.state} to ${order.to.city}, ${order.to.state}, ${formatCentsWhole(order.deliveredCents)} delivered.`}
        </li>
      ))}
    </ul>
  );
}

export function LandingHeroScene() {
  const { tick, animated } = useOrderTimeline();
  const orders: ActiveOrder[] = activeOrders(tick);
  return (
    <div className="@container overflow-hidden rounded-3xl border border-border bg-surface shadow-2xl shadow-foreground/10">
      <ExampleSummary />
      <div className="relative bg-muted/30 px-2 py-6 sm:px-4 sm:py-10">
        <div aria-hidden className="relative">
          <svg viewBox={`0 0 ${US_MAP_WIDTH} ${US_MAP_HEIGHT}`} className="block h-auto w-full overflow-visible">
            <path d={US_DOT_PATH} className="stroke-foreground/25" strokeWidth={4} strokeLinecap="round" />
            <NetworkLanes />
            {orders.map((active) => (
              <ActiveLane key={active.order.id} order={active.order} phase={active.phase} animated={animated} />
            ))}
          </svg>
          <MapCards orders={orders} />
        </div>
        <span aria-hidden className="absolute bottom-3 left-4 text-xs font-medium text-fg-muted sm:left-6">Example orders</span>
      </div>
      <PhoneCards orders={orders} />
    </div>
  );
}
